import { FormControl, FormLabel, RadioGroup, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import { Modal } from '../../../Modal'
import { SurveyRadioChoice } from './SurveyRadioChoice'
import { styles } from './styles'
import { type SurveyRadio } from './SurveyRadioModel'

interface Props {
  question: SurveyRadio
}

export const SurveyRadioContent = ({ question }: Props): JSX.Element => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [pendingValue, setPendingValue] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(question.uploadedFileName)
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(question.uploadedFileKey)
  const selectedValue = question.value

  useEffect(() => {
    const updateFileState = (): void => {
      setUploadedFileName(question.uploadedFileName)
      setUploadedFileKey(question.uploadedFileKey)
    }

    question.onPropertyChanged.add((_sender, options) => {
      if (options.name === 'uploadedFileName' || options.name === 'uploadedFileKey') {
        updateFileState()
      }
    })

    return () => {
      question.onPropertyChanged.clear()
    }
  }, [question])

  const handleConfirmDelete = async (): Promise<void> => {
    await question.deleteFile()
    setUploadedFileName(null)
    setUploadedFileKey(null)
    if (pendingValue !== null) {
      question.value = pendingValue
      setPendingValue(null)
    }
    setIsDeleteModalOpen(false)
  }

  const handleRadioChange = (newValue: string): void => {
    const currentChoice = question.mappedChoices.find(c => c.value === selectedValue)
    const newChoice = question.mappedChoices.find(c => c.value === newValue)
    const hasUploadedFile = uploadedFileName != null && uploadedFileName !== ''

    const switchingFromUploadToNonUpload = (currentChoice?.hasUpload === true) && (newChoice?.hasUpload === false) && hasUploadedFile

    if (switchingFromUploadToNonUpload) {
      setPendingValue(newValue)
      setIsDeleteModalOpen(true)
    } else {
      question.value = newValue
    }
  }

  return (
    <>
      <FormControl component="fieldset">
        <FormLabel>{question.fullTitle}</FormLabel>
        <RadioGroup
          name={question.name}
          value={selectedValue ?? ''}
          onChange={(e) => { handleRadioChange(e.target.value) }}
        >
          {question.mappedChoices.map((choice) => (
            <SurveyRadioChoice
              key={choice.value}
              choice={choice}
              isSelected={selectedValue === choice.value}
              question={question}
              onDeleteFile={() => { setIsDeleteModalOpen(true) }}
              uploadedFileName={uploadedFileName}
              uploadedFileKey={uploadedFileKey}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {isDeleteModalOpen && (
        <Modal
          open={true}
          title="Remove Related Materials"
          onClose={() => {
            setIsDeleteModalOpen(false)
            setPendingValue(null)
          }}
          onConfirm={() => { void handleConfirmDelete() }}
          confirmButtonText="Remove Uploaded Files"
          bodyContent={(
            <>
              <Typography variant="body1" fontWeight="bold">Are you sure you want to change your related materials upload selection to "no"?</Typography>
              <Typography variant="body1" sx={styles.modalWarningText}>If you select "no" now, your uploads will be lost.</Typography>
            </>
          )}
        />
      )}
    </>
  )
}
